import { isNative } from '../platform';

export interface PhotoResult {
  base64:   string;
  mimeType: string;
  dataUrl:  string;
}

/**
 * On native: opens "Câmera / Galeria" action sheet via CameraSource.Prompt.
 * On web: opens file input picker.
 * Returns null if user cancels.
 * NEVER saves to device storage. NEVER uploads to Supabase Storage.
 */
export async function takeOrPickPhoto(): Promise<PhotoResult | null> {
  if (isNative) {
    try {
      const { Camera, CameraSource, CameraResultType } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        source:             CameraSource.Prompt,
        quality:            80,
        resultType:         CameraResultType.Base64,
        correctOrientation: true,
        promptLabelHeader:  'Selecionar foto',
        promptLabelPhoto:   'Galeria',
        promptLabelPicture: 'Câmera',
        promptLabelCancel:  'Cancelar',
        saveToGallery:      false,
      });
      if (!photo.base64String) return null;
      const mimeType = `image/${photo.format ?? 'jpeg'}`;
      return {
        base64:  photo.base64String,
        mimeType,
        dataUrl: `data:${mimeType};base64,${photo.base64String}`,
      };
    } catch {
      return null;
    }
  }

  // Web fallback: hidden file input
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type   = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) { resolve(null); return; }
      const buf   = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary  = '';
      bytes.forEach(b => { binary += String.fromCharCode(b); });
      const base64 = btoa(binary);
      resolve({ base64, mimeType: file.type, dataUrl: `data:${file.type};base64,${base64}` });
    };
    // fallback if cancel event isn't fired
    setTimeout(() => { if (!input.files?.length) resolve(null); }, 60_000);
    input.click();
  });
}
